/** 反馈浮层：原生 DOM，分类 + 文本 + 可选联系方式 + 自动对局上下文。 */
import {
  submitFeedback,
  type FeedbackCategory,
  type FeedbackContext,
} from '../net/feedback';
import type { ClientIdentity } from '../net/identity';

interface FeedbackEls {
  screen: HTMLElement;
  cats: HTMLElement;
  text: HTMLTextAreaElement;
  contact: HTMLInputElement;
  ctxLine: HTMLElement;
  status: HTMLElement;
  submit: HTMLButtonElement;
  cancel: HTMLButtonElement;
}

const CAT_LABEL: Record<FeedbackCategory, string> = {
  bug: 'BUG',
  balance: '平衡',
  feel: '手感',
  idea: '建议',
  other: '其他',
};
const CAT_ORDER: FeedbackCategory[] = [
  'bug', 'balance', 'feel', 'idea', 'other',
];

export class FeedbackModal {
  private category: FeedbackCategory = 'bug';
  private context: FeedbackContext | null = null;

  constructor(
    private els: FeedbackEls,
    private identityProvider: () => ClientIdentity | null,
    private contextProvider: () => FeedbackContext | null,
  ) {
    for (const c of CAT_ORDER) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'fb-cat';
      b.textContent = CAT_LABEL[c];
      b.addEventListener('click', () => {
        this.category = c;
        this.markCats();
      });
      this.els.cats.appendChild(b);
    }
    this.els.submit.addEventListener('click', () => void this.onSubmit());
    this.els.cancel.addEventListener('click', () => this.close());
  }

  /** 绑定各入口按钮（菜单 / 两个结算屏）。 */
  bind(...ids: string[]): void {
    for (const id of ids) {
      document.getElementById(id)?.addEventListener('click', () => this.open());
    }
  }

  open(): void {
    this.context = this.contextProvider();
    this.els.text.value = '';
    this.els.contact.value = '';
    this.els.status.textContent = '';
    this.els.submit.disabled = false;
    this.category = 'bug';
    this.markCats();
    this.els.ctxLine.textContent = this.context
      ? `将自动附带对局上下文：${this.context.date ?? ''} ` +
        `${this.context.mode ?? ''} · 种子 ${this.context.seed ?? '?'} · ` +
        `${this.context.kills ?? 0} 杀 · ${this.context.time ?? 0}s` +
        (this.context.cause ? ` · 死因 ${this.context.cause}` : '')
      : '无最近对局上下文（菜单反馈）';
    this.els.screen.classList.remove('hidden');
  }

  close(): void {
    this.els.screen.classList.add('hidden');
  }

  private markCats(): void {
    for (const b of Array.from(this.els.cats.children)) {
      b.classList.toggle('fb-cat-on', b.textContent === CAT_LABEL[this.category]);
    }
  }

  private async onSubmit(): Promise<void> {
    const text = this.els.text.value.trim();
    if (text.length < 1) {
      this.els.status.textContent = '请先填写反馈内容';
      return;
    }
    const identity = this.identityProvider();
    if (!identity || !identity.secret) {
      this.els.status.textContent = '身份尚未建立，无法提交，请刷新后重试';
      return;
    }
    this.els.submit.disabled = true;
    this.els.status.textContent = '提交中…';
    try {
      await submitFeedback(identity, {
        category: this.category,
        text,
        contact: this.els.contact.value.trim(),
        context: this.context ?? {},
      });
      this.els.status.textContent = '';
      this.els.ctxLine.textContent = '已收到，感谢反馈 ✦';
      this.els.text.value = '';
      this.els.contact.value = '';
      this.els.submit.disabled = true;
      setTimeout(() => this.close(), 1200);
    } catch (err) {
      this.els.status.textContent = `提交失败：${String((err as Error).message)}`;
      this.els.submit.disabled = false;
    }
  }
}
